import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaStore, FaWifi, FaExclamationTriangle, FaTimesCircle, FaSearch, FaEraser } from 'react-icons/fa';
import EstadoLocal from './EstadoLocal';
import estadoCajeros from '../../api/apiweb-estadoCajeros';
import estadoConexionSegunLastCheck from './estadoConexionSegunLastCheck';
import EstadosConexion from './EstadosConexion';
import './EstadoCajeros.scss';

function EstadoCajeros() {
  const [dataLocales, setDataLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');

  useEffect(() => {
    estadoCajeros.getEstadoCajeros().then((response) => {
      setDataLocales(agrupaPorLocal(response.data));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const agrupaPorLocal = (data) => {
    // Equipos sin local (null, undefined o vacío) se agrupan bajo una clave vacía
    // para que EstadoLocal muestre "Sin local asignado" y no se cuenten como local.
    const locales = data.reduce((acc, item) => {
      const local = item.local && String(item.local).trim() ? item.local : '';
      if (!acc[local]) acc[local] = [];
      acc[local].push(item);
      return acc;
    }, {});
    return Object.keys(locales).map((local) => ({
      name: local,
      cajas: locales[local].map((device) => ({
        ...device,
        estado: estadoConexionSegunLastCheck(device.lastcheck),
      })),
    }));
  };

  // El grupo "Sin local asignado" (name vacío) no se cuenta como local.
  const localesReales = dataLocales.filter((local) => local.name).length;

  // Filtra cajas por nombre de equipo y estado; descarta locales sin coincidencias.
  const termino = busqueda.trim().toLowerCase();
  const localesFiltrados = dataLocales
    .map((local) => ({
      ...local,
      cajas: local.cajas.filter((caja) => {
        const coincideNombre = (caja.device_name || '').toLowerCase().includes(termino);
        const coincideEstado = !estadoFiltro || caja.estado === estadoFiltro;
        return coincideNombre && coincideEstado;
      }),
    }))
    .filter((local) => local.cajas.length > 0);

  const hayFiltros = termino !== '' || estadoFiltro !== '';

  const limpiarFiltros = () => {
    setBusqueda('');
    setEstadoFiltro('');
  };

  const totals = dataLocales.reduce(
    (acc, local) => {
      local.cajas.forEach((caja) => {
        acc.total += 1;
        if (caja.estado === EstadosConexion.Conectado) acc.conectados += 1;
        else if (caja.estado === EstadosConexion.Alerta) acc.alerta += 1;
        else acc.sinConexion += 1;
      });
      return acc;
    },
    { total: 0, conectados: 0, alerta: 0, sinConexion: 0 },
  );

  return (
    <div className="EstadoCajeros">
      <div className="page-header">
        <h1 className="page-title">Estado de Cajeros</h1>
        <p className="page-subtitle">Monitoreo en tiempo real de cajas por local</p>
      </div>

      {!loading && dataLocales.length > 0 && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon stat-icon-neutral"><FaStore /></div>
            <div>
              <div className="stat-value">{localesReales}</div>
              <div className="stat-label">{localesReales === 1 ? 'Local' : 'Locales'}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-success"><FaWifi /></div>
            <div>
              <div className="stat-value">{totals.conectados}<span className="stat-divider">/{totals.total}</span></div>
              <div className="stat-label">Conectados</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-warning"><FaExclamationTriangle /></div>
            <div>
              <div className="stat-value">{totals.alerta}</div>
              <div className="stat-label">En alerta</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon stat-icon-danger"><FaTimesCircle /></div>
            <div>
              <div className="stat-value">{totals.sinConexion}</div>
              <div className="stat-label">Sin conexión</div>
            </div>
          </div>
        </div>
      )}

      {!loading && dataLocales.length > 0 && (
        <div className="filters-bar">
          <div className="filter-search">
            <FaSearch className="filter-search-icon" />
            <input
              type="text"
              placeholder="Buscar equipo por nombre…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={estadoFiltro}
            onChange={(e) => setEstadoFiltro(e.target.value)}
            aria-label="Filtrar por estado"
          >
            <option value="">Todos los estados</option>
            <option value={EstadosConexion.Conectado}>Conectado</option>
            <option value={EstadosConexion.Alerta}>Alerta</option>
            <option value={EstadosConexion.SinConexion}>Sin conexión</option>
          </select>
          {hayFiltros && (
            <button type="button" className="filter-clear" onClick={limpiarFiltros}>
              <FaEraser className="me-2" />Limpiar
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <Spinner animation="border" variant="primary" />
          <p>Cargando estado de cajeros…</p>
        </div>
      ) : dataLocales.length === 0 ? (
        <div className="empty-state">
          <FaStore className="empty-state-icon" />
          <p className="empty-state-text">No hay locales registrados</p>
        </div>
      ) : localesFiltrados.length === 0 ? (
        <div className="empty-state">
          <FaSearch className="empty-state-icon" />
          <p className="empty-state-text">No se encontraron equipos con los filtros aplicados</p>
        </div>
      ) : (
        <div className="locales-list">
          {localesFiltrados.map((local) => (
            <EstadoLocal key={local.name || 'sin-local'} local={local} />
          ))}
        </div>
      )}
    </div>
  );
}

export default EstadoCajeros;
